/**
 * Numbers as a person reads them.
 *
 * Units on the wire are bytes, seconds or percent (knowledge doc section 5), and
 * every one of those is unreadable at fleet scale without scaling: `disk_used_bytes`
 * on a modern disk is a thirteen-digit number.
 */

const BYTE_UNITS = ['B', 'kB', 'MB', 'GB', 'TB', 'PB'] as const;

/**
 * Decimal units, not binary.
 *
 * `df`, cloud consoles and disk labels all quote decimal gigabytes, so a console
 * that quietly reports GiB under the name GB disagrees with everything an operator
 * would check it against.
 */
export function formatBytes(value: number): string {
  if (!Number.isFinite(value)) return '—';
  const sign = value < 0 ? '-' : '';
  let size = Math.abs(value);
  let unit = 0;

  while (size >= 1000 && unit < BYTE_UNITS.length - 1) {
    size /= 1000;
    unit += 1;
  }

  // Three significant figures: `4.23 GB` and `842 MB` are both as precise as the
  // reading behind them, and neither runs off the end of a table cell.
  const digits = unit === 0 ? 0 : size >= 100 ? 0 : size >= 10 ? 1 : 2;
  return `${sign}${size.toFixed(digits)} ${BYTE_UNITS[unit] ?? 'B'}`;
}

export function formatPercent(value: number, digits = 1): string {
  if (!Number.isFinite(value)) return '—';
  return `${value.toFixed(digits)}%`;
}

/**
 * A forecast horizon in words (section 8.3's "full in N days").
 *
 * Days are the unit the estimate is calculated in, but "full in 0.03 days" is not
 * a sentence, so anything under a day becomes hours and anything under an hour
 * becomes the one answer that matters at that point.
 */
export function formatHorizon(days: number): string {
  if (days <= 0) return 'now';
  if (days < 1 / 24) return 'under an hour';
  if (days < 1) {
    const hours = Math.round(days * 24);
    return `${String(hours)} hour${hours === 1 ? '' : 's'}`;
  }
  if (days < 14) {
    const rounded = Math.round(days * 10) / 10;
    return `${String(rounded)} day${rounded === 1 ? '' : 's'}`;
  }
  return `${String(Math.round(days))} days`;
}

/** A rate, with its sign carried by the word rather than only by a minus. */
export function formatRate(changePerDay: number, format: (value: number) => string): string {
  if (changePerDay === 0) return 'steady';
  const magnitude = format(Math.abs(changePerDay));
  return `${changePerDay > 0 ? '+' : '−'}${magnitude} a day`;
}
